import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { styles } from '../styles/themes/style';
import { phoneCountryData, PhoneCountry } from '../assets/utils/phoneCountryData';
import SvgFragment from '../fragments/SvgFragment';
import ReactCountryFlag from 'react-country-flag';

interface PhoneInputProps {
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEnter?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  className?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  placeholder = 'e.g. 702 123 4567',
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  onEnter,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  className
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(() => 
    phoneCountryData.find(country => country.code === 'US') || phoneCountryData[0]
  ); // Default to US
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const parsePhoneNumber = useCallback((fullNumber: string): { country: PhoneCountry; number: string } => {
    // Remove any non-digit characters except +
    const cleanNumber = fullNumber.replace(/[^\d+]/g, '');
    
    // Sort phoneCountryData by dial code length (longest first) to properly match multi-digit codes
    const sortedCountries = [...phoneCountryData].sort((a, b) => b.dialCode.length - a.dialCode.length);
    
    // Find matching country by dial code (try longest codes first)
    for (const country of sortedCountries) {
      if (cleanNumber.startsWith(country.dialCode)) {
        return {
          country,
          number: cleanNumber.substring(country.dialCode.length)
        };
      }
    }
    
    // If no country found, assume it's just the number part
    return {
      country: selectedCountry,
      number: cleanNumber
    };
  }, [selectedCountry]);

  // Parse initial value to extract country and phone number
  useEffect(() => {
    if (value) {
      const parsed = parsePhoneNumber(value);
      if (parsed.country) {
        setSelectedCountry(parsed.country);
      }
      setPhoneNumber(parsed.number);
    }
  }, [value, parsePhoneNumber]);

  const handleCountrySelect = (country: PhoneCountry) => {
    if (disabled) return;
    
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    
    // Update the full phone number
    const fullNumber = country.dialCode + phoneNumber;
    onChange?.(fullNumber);
    
    // Focus the input after selecting a country - use a small delay to avoid layout shift
    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const inputValue = e.target.value;
    
    // Only allow digits, spaces, parentheses, hyphens
    const cleanValue = inputValue.replace(/[^0-9\s()-]/g, '');
    setPhoneNumber(cleanValue);
    
    // Update the full phone number
    const fullNumber = selectedCountry.dialCode + cleanValue;
    onChange?.(fullNumber);
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsFocused(true);
      onFocus?.();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't blur if clicking on dropdown
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    
    setIsFocused(false);
    setIsDropdownOpen(false);
    onBlur?.();
  };

  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const handleDropdownToggle = () => {
    if (!disabled) {
      if (!isDropdownOpen) {
        updateDropdownPosition();
      }
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
    
    if (e.key === 'Enter') {
      // Call onEnter if provided
      onEnter?.();
      // Blur the input after Enter
      inputRef.current?.blur();
    }
    
    // Call parent's onKeyDown handler if provided
    onKeyDown?.(e);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside dropdown or on the country selector button
      const isClickInDropdown = dropdownRef.current?.contains(target);
      const isClickOnContainer = containerRef.current?.contains(target);
      
      if (!isClickInDropdown && isClickOnContainer) {
        // If click is on container (country selector or input) but not in dropdown, allow toggle
        return;
      }
      
      if (!isClickInDropdown && !isClickOnContainer) {
        // Click is outside everything - close dropdown
        setIsDropdownOpen(false);
      }
    };

    const handleScroll = () => {
      if (isDropdownOpen) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (isDropdownOpen) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDropdownOpen]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <PhoneInputContainer 
      ref={containerRef}
      className={className} 
      $isFocused={isFocused} 
      $disabled={disabled}
      onClick={handleContainerClick}
    >
      <CountrySelector 
        onClick={handleDropdownToggle}
        $isOpen={isDropdownOpen}
        $disabled={disabled}
        type="button"
      >
        <CountryFlag>
          <ReactCountryFlag 
            countryCode={selectedCountry.code} 
            svg 
            style={{
              width: '14px',
              height: '10px',
              borderRadius: '2px'
            }}
          />
        </CountryFlag>
        <CountryCode $isEmpty={!phoneNumber && !value}>{selectedCountry.dialCode}</CountryCode>
        <DropdownArrow $isOpen={isDropdownOpen} $isEmpty={!phoneNumber && !value}>
          <SvgFragment type="arrowDown" />
        </DropdownArrow>
      </CountrySelector>
      
      <PhoneNumberInput
        ref={inputRef}
        type="text"
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      
      {isDropdownOpen && containerRef.current && createPortal(
        <DropdownContainer 
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 10000
          }}
        >
          <DropdownList>
            {phoneCountryData.map((country) => (
              <DropdownItem
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                $isSelected={country.code === selectedCountry.code}
              >
                <CountryFlag>
                  <ReactCountryFlag 
                    countryCode={country.code} 
                    svg 
                    style={{
                      width: '16px',
                      height: '12px',
                      borderRadius: '2px'
                    }}
                  />
                </CountryFlag>
                <CountryName>{country.name}</CountryName>
                <CountryCode>{country.dialCode}</CountryCode>
              </DropdownItem>
            ))}
          </DropdownList>
        </DropdownContainer>,
        document.body
      )}
    </PhoneInputContainer>
  );
};

const PhoneInputContainer = styled.div<{ $isFocused: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 32px;
  border: 1px solid ${({ $isFocused }) => $isFocused ? styles.colors.primary600 : styles.colors.black200};
  border-radius: 6px;
  background-color: ${styles.colors.white};
  overflow: hidden;
  box-sizing: border-box;
  
  ${({ $disabled }) => $disabled && `
    background-color: ${styles.colors.black200};
    border-color: ${styles.colors.black200};
    cursor: not-allowed;
  `}
  
  &:hover {
    border-color: ${({ $disabled, $isFocused }) => 
      $disabled ? styles.colors.black200 : 
      $isFocused ? styles.colors.primary600 : 
      styles.colors.primary600
    };
  }
  
  &:focus-within {
    border-color: ${styles.colors.primary600};
    box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
  }
`;

const CountrySelector = styled.button<{ $isOpen: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 6px;
  border: none;
  background: none;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  min-width: 80px;
  height: 100%;
  
  &:hover {
    background-color: ${({ $disabled }) => $disabled ? 'transparent' : styles.colors.primary100};
  }
  
  &:active {
    background-color: ${({ $disabled }) => $disabled ? 'transparent' : styles.colors.primary200};
  }
`;

const CountryFlag = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 16px;
  flex-shrink: 0;
`;

const CountryCode = styled.span<{ $isEmpty?: boolean }>`
  min-width: 34px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $isEmpty }) => $isEmpty ? styles.colors.black300 : styles.colors.black600};
  white-space: nowrap;
`;

const DropdownArrow = styled.div<{ $isOpen: boolean; $isEmpty?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  
  svg {
    width: 12px;
    height: 12px;
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'none'};
    transition: transform 0.2s ease;
    
    path {
      fill: ${({ $isEmpty }) => $isEmpty ? styles.colors.black300 : styles.colors.black400};
    }
  }
`;

const PhoneNumberInput = styled.input`
  flex: 1;
  height: 100%;
  min-width: 120px;
  border: none;
  font-size: 14px;
  line-height: 20px;
  color: ${styles.colors.black600};
  background-color: transparent;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: ${styles.colors.black300};
  }
  
  &:disabled {
    cursor: not-allowed;
    color: ${styles.colors.black400};
  }
  
  &:read-only {
    cursor: pointer;
    color: ${styles.colors.black600};
  }
`;

const DropdownContainer = styled.div`
  background: ${styles.colors.white};
  border: 1px solid ${styles.colors.black200};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 2px;
  max-height: 200px;
  overflow-y: auto;
`;

const DropdownList = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  background-color: ${({ $isSelected }) => $isSelected ? styles.colors.primary100 : 'transparent'};
  
  &:hover {
    background-color: ${({ $isSelected }) => 
      $isSelected ? styles.colors.primary100 : styles.colors.primary100
    };
  }
  
  &:active {
    background-color: ${styles.colors.primary200};
  }
`;

const CountryName = styled.span`
  flex: 1;
  font-size: 14px;
  color: ${styles.colors.black600};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default PhoneInput;
